import { useState } from 'react'
import { router } from '@inertiajs/react'
import Modal from '../shared/Modal'
import { User, MetricCategory } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  isOpen: boolean
  onClose: () => void
  teamId: number
  teamMembers: User[]
  categories: MetricCategory[]
  // When rendered inside a live meeting, the create POST redirects back to
  // the meeting page; preserveState keeps the meeting's local component
  // state (timers, viewed section) intact while props refresh.
  preserveState?: boolean
  // The live meeting scoreboard is a weekly grid, so metrics created from
  // inside a meeting are locked to Weekly. Other cadences are managed on
  // the standalone Scoreboard page.
  weeklyOnly?: boolean
}

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]

const FORMAT_OPTIONS = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
]

const COMPARISON_OPTIONS = [
  { value: 'gte', label: '>= (Greater than or equal)' },
  { value: 'lte', label: '<= (Less than or equal)' },
  { value: 'gt', label: '> (Greater than)' },
  { value: 'lt', label: '< (Less than)' },
  { value: 'eq', label: '= (Equal to)' },
]

export default function AddMetricModal({ isOpen, onClose, teamId, teamMembers, categories, preserveState = false, weeklyOnly = false }: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [frequency, setFrequency] = useState('weekly')
  const [format, setFormat] = useState('number')
  const [comparison, setComparison] = useState('gte')
  const [target, setTarget] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [decimalPlaces, setDecimalPlaces] = useState<number>(0)
  const [abbreviateLargeNumbers, setAbbreviateLargeNumbers] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetForm = () => {
    setName('')
    setDescription('')
    setOwnerId('')
    setFrequency('weekly')
    setFormat('number')
    setComparison('gte')
    setTarget('')
    setCategoryId('')
    setDecimalPlaces(0)
    setAbbreviateLargeNumbers(true)
    setError(null)
  }

  const handleSave = () => {
    setSaving(true)
    setError(null)
    router.post(
      `/teams/${teamId}/scoreboard/metrics`,
      {
        metric: {
          name,
          description: description || null,
          owner_id: ownerId || null,
          frequency,
          format,
          comparison,
          target: target ? parseFloat(target) : null,
          category_id: categoryId || null,
          decimal_places: decimalPlaces,
          abbreviate_large_numbers: abbreviateLargeNumbers,
        },
      },
      {
        preserveScroll: true,
        // Keep local component state (the typed-in form) when the server
        // responds with validation errors, even on the standalone scoreboard
        // page where preserveState is otherwise false.
        preserveState: preserveState || 'errors',
        onError: errors => {
          const messages = Object.entries(errors).flatMap(([field, msgs]) =>
            [msgs].flat().filter(Boolean).map(msg => {
              const label = field.replace(/_/g, ' ')
              return `${label.charAt(0).toUpperCase()}${label.slice(1)} ${msg}`
            })
          )
          setError(messages.length > 0 ? messages.join(', ') : 'Could not save the metric. Please check the form and try again.')
        },
        onSuccess: () => {
          resetForm()
          onClose()
        },
        onFinish: () => {
          setSaving(false)
        },
      }
    )
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Metric">
      <div className="space-y-4">
        {error && (
          <div className="bg-status-off-track/10 border border-status-off-track/30 text-status-off-track text-sm rounded-lg px-3 py-2" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Weekly Revenue"
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={2}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Owner</label>
          <select
            value={ownerId}
            onChange={e => setOwnerId(e.target.value)}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
          >
            <option value="">Unassigned</option>
            {sortPeople(teamMembers).map(m => (
              <option key={m.id} value={m.id}>{m.full_name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Frequency</label>
            {weeklyOnly ? (
              <>
                <div className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary bg-stone/10">
                  Weekly
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  Meeting scoreboards track weekly metrics. Create monthly, quarterly, or annual metrics from the Scoreboard page.
                </p>
              </>
            ) : (
              <select
                value={frequency}
                onChange={e => setFrequency(e.target.value)}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
              >
                {FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Format</label>
            <select
              value={format}
              onChange={e => setFormat(e.target.value)}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              {FORMAT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Decimal places</label>
            <select
              value={decimalPlaces}
              onChange={e => setDecimalPlaces(parseInt(e.target.value, 10))}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              <option value={0}>0</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={abbreviateLargeNumbers}
                onChange={e => setAbbreviateLargeNumbers(e.target.checked)}
                className="w-4 h-4 rounded border-stone/30 text-pinnacle-red focus:ring-pinnacle-red/30"
              />
              Abbreviate large numbers
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Comparison</label>
            <select
              value={comparison}
              onChange={e => setComparison(e.target.value)}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              {COMPARISON_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Target</label>
            <input
              type="number"
              step="any"
              value={target}
              onChange={e => setTarget(e.target.value)}
              placeholder="e.g., 100"
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Category</label>
          <select
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
          >
            <option value="">No Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Creating...' : 'Create Metric'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
